import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'
import VideoFileRoundedIcon from '@mui/icons-material/VideoFileRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import PortalVoiceCallButton from 'src/mentor/components/voice/PortalVoiceCallButton'
import {
  getMentorCommunicationTargets,
  getMentorPatientWhatsappThread,
  sendMentorPatientWhatsappAttachment,
  sendMentorPatientWhatsappMessage,
  sendMentorPatientWhatsappTemplate,
} from 'src/mentor/services/practitionerContactService'

dayjs.extend(utc)

const TAB_ORDER = ['patient', 'practitioner', 'support']

const WHATSAPP_ATTACHMENT_OPTIONS = [
  {
    id: 'media',
    label: 'Photos & videos',
    helper: 'Images and video clips',
    accept: 'image/*,video/*',
    kind: 'media',
  },
  {
    id: 'document',
    label: 'Document',
    helper: 'PDF, DOC, XLS and more',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip,.rar,.7z',
    kind: 'document',
  },
  {
    id: 'audio',
    label: 'Audio',
    helper: 'Voice notes and audio files',
    accept: 'audio/*',
    kind: 'audio',
  },
  {
    id: 'all',
    label: 'Any file',
    helper: 'Fallback for other attachments',
    accept: '*/*',
    kind: 'document',
  },
]

function parseThreadTimestamp(value) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const numericDate = new Date(value < 1e12 ? value * 1000 : value)
    return Number.isNaN(numericDate.getTime()) ? null : numericDate
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    if (/^\d{10,13}$/.test(trimmed)) {
      const numericValue = Number(trimmed)
      const numericDate = new Date(trimmed.length <= 10 ? numericValue * 1000 : numericValue)
      return Number.isNaN(numericDate.getTime()) ? null : numericDate
    }
    const directDate = new Date(trimmed)
    if (!Number.isNaN(directDate.getTime())) {
      return directDate
    }
    const normalizedDate = new Date(trimmed.replace(' ', 'T'))
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate
  }

  if (typeof value === 'object') {
    if (value?.$date) {
      return parseThreadTimestamp(value.$date)
    }
    if (value?.epochSecond !== undefined) {
      const milliseconds = (Number(value.epochSecond) * 1000) + Math.floor(Number(value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.seconds !== undefined) {
      const milliseconds = (Number(value.seconds) * 1000) + Math.floor(Number(value.nanos || value.nano || 0) / 1000000)
      return parseThreadTimestamp(milliseconds)
    }
    if (value?.time !== undefined) {
      return parseThreadTimestamp(value.time)
    }
    if (value?.value !== undefined) {
      return parseThreadTimestamp(value.value)
    }
  }

  return null
}

function formatThreadTimestamp(value) {
  const date = parseThreadTimestamp(value)
  if (!date) {
    return ''
  }

  const now = new Date()
  const isSameYear = now.getFullYear() === date.getFullYear()
  const isSameDay = now.getFullYear() === date.getFullYear()
    && now.getMonth() === date.getMonth()
    && now.getDate() === date.getDate()

  return new Intl.DateTimeFormat('en-IN', {
    ...(isSameDay ? {} : { day: 'numeric', month: 'short' }),
    ...(isSameYear || isSameDay ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

function formatTargetPhone(phoneNumber) {
  if (!phoneNumber) {
    return ''
  }
  return formatPhoneNumber(phoneNumber)?.concatenatedString || phoneNumber
}

function getTabMeta(tabKey) {
  if (tabKey === 'patient') {
    return {
      label: 'Patient',
      icon: <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />,
      accentColor: '#2563eb',
      bgColor: 'linear-gradient(135deg, rgba(219, 234, 254, 0.95) 0%, rgba(239, 246, 255, 0.92) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.18)',
      badgeBg: 'rgba(37, 99, 235, 0.12)',
    }
  }
  if (tabKey === 'practitioner') {
    return {
      label: 'Practitioner',
      icon: <LocalHospitalRoundedIcon sx={{ fontSize: 18 }} />,
      accentColor: '#7c3aed',
      bgColor: 'linear-gradient(135deg, rgba(245, 243, 255, 0.95) 0%, rgba(250, 245, 255, 0.92) 100%)',
      borderColor: 'rgba(124, 58, 237, 0.16)',
      badgeBg: 'rgba(124, 58, 237, 0.12)',
    }
  }
  return {
    label: 'Support',
    icon: <SupportAgentRoundedIcon sx={{ fontSize: 18 }} />,
    accentColor: '#0f766e',
    bgColor: 'linear-gradient(135deg, rgba(240, 253, 250, 0.95) 0%, rgba(236, 253, 245, 0.92) 100%)',
    borderColor: 'rgba(13, 148, 136, 0.16)',
    badgeBg: 'rgba(13, 148, 136, 0.12)',
  }
}

function getTemplateActionFriendlyLabel(actionType, fallbackLabel) {
  if (fallbackLabel) {
    return fallbackLabel
  }
  if (actionType === 'permission-template') {
    return 'Conversation opener'
  }
  if (actionType === 'call-heads-up') {
    return 'Call heads-up'
  }
  if (actionType === 'inbound-reply') {
    return 'Reply'
  }
  if (actionType === 'free-form') {
    return 'Message'
  }
  return 'Interaction'
}

function getTemplateActionChipColor(actionType) {
  if (actionType === 'call-heads-up') {
    return 'warning'
  }
  if (actionType === 'permission-template') {
    return 'info'
  }
  if (actionType === 'inbound-reply') {
    return 'secondary'
  }
  return 'default'
}

function getDeliveryStatusMeta(status) {
  const normalized = `${status || ''}`.toLowerCase()
  if (normalized === 'read') {
    return {
      label: 'Read',
      color: '#2563eb',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'delivered') {
    return {
      label: 'Delivered',
      color: '#0f766e',
      icon: <DoneAllRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'sent' || normalized === 'queued' || normalized === 'accepted') {
    return {
      label: normalized === 'queued' ? 'Queued' : 'Sent',
      color: '#475569',
      icon: normalized === 'queued'
        ? <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
        : <DoneRoundedIcon sx={{ fontSize: 16 }} />,
    }
  }
  if (normalized === 'failed' || normalized === 'undelivered') {
    return {
      label: normalized === 'undelivered' ? 'Undelivered' : 'Failed',
      color: '#dc2626',
      icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 15 }} />,
    }
  }
  return {
    label: status || 'Sending',
    color: '#475569',
    icon: <ScheduleRoundedIcon sx={{ fontSize: 15 }} />,
  }
}

function getPendingAttachmentKind(file) {
  const mimeType = `${file?.type || ''}`.toLowerCase()
  const fileName = `${file?.name || ''}`.toLowerCase()
  if (mimeType.startsWith('image/')) {
    return 'image'
  }
  if (mimeType.startsWith('video/')) {
    return 'video'
  }
  if (mimeType.startsWith('audio/')) {
    return 'audio'
  }
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileName)) {
    return 'image'
  }
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(fileName)) {
    return 'video'
  }
  if (/\.(mp3|wav|aac|ogg|m4a)$/i.test(fileName)) {
    return 'audio'
  }
  return 'document'
}

function formatPendingAttachmentSize(sizeInBytes) {
  const size = Number(sizeInBytes || 0)
  if (!size) {
    return ''
  }
  if (size < 1024) {
    return `${size} B`
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(size < 10 * 1024 ? 1 : 0)} KB`
  }
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

function describePendingAttachment(file) {
  const kind = getPendingAttachmentKind(file)
  const typeLabel = kind === 'image'
    ? 'Photo'
    : kind === 'video'
      ? 'Video'
      : kind === 'audio'
        ? 'Audio'
        : 'Document'
  const sizeLabel = formatPendingAttachmentSize(file?.size)
  return {
    kind,
    secondaryLabel: sizeLabel ? `${typeLabel} · ${sizeLabel}` : typeLabel,
  }
}

function getComposerAttachmentIcon(kind, size = 18) {
  if (kind === 'media' || kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: size }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: size }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: size }} />
}

function getAttachmentIcon(kind) {
  if (kind === 'image') {
    return <ImageRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'audio') {
    return <AudiotrackRoundedIcon sx={{ fontSize: 18 }} />
  }
  if (kind === 'video') {
    return <VideoFileRoundedIcon sx={{ fontSize: 18 }} />
  }
  return <DescriptionRoundedIcon sx={{ fontSize: 18 }} />
}

function normalizeAttachmentUrl(attachment) {
  return attachment?.previewUrl || attachment?.downloadUrl || attachment?.objectUrl || attachment?.url || ''
}

function getAttachmentKind(attachment) {
  if (attachment?.kind) {
    return attachment.kind
  }
  return getPendingAttachmentKind({
    type: attachment?.mimeType,
    name: attachment?.fileName,
  })
}

function splitTrailingUrlToken(token = '') {
  let cleanToken = `${token || ''}`
  let trailingText = ''
  while (/[),.;!?]$/.test(cleanToken)) {
    trailingText = cleanToken.slice(-1) + trailingText
    cleanToken = cleanToken.slice(0, -1)
  }
  return {
    cleanToken,
    trailingText,
  }
}

function normalizeExternalUrl(url = '') {
  if (!url) {
    return ''
  }
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function extractMessageUrls(text = '') {
  const matches = `${text || ''}`.match(/(?:https?:\/\/|www\.)[^\s<]+/gi) || []
  const seen = new Set()
  return matches
    .map((item) => splitTrailingUrlToken(item).cleanToken)
    .filter((item) => {
      if (!item || seen.has(item)) {
        return false
      }
      seen.add(item)
      return true
    })
}

function isImageLikeUrl(url = '') {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(?:[?#].*)?$/i.test(url)
}

function renderWhatsappMarkup(text) {
  if (!text) {
    return null
  }

  const segments = []
  const regex = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|(?:https?:\/\/|www\.)[^\s<]+)/g
  let lastIndex = 0
  let match
  let keyIndex = 0

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        <Fragment key={`plain-${keyIndex}`}>
          {text.slice(lastIndex, match.index)}
        </Fragment>,
      )
      keyIndex += 1
    }

    const token = match[0]
    const wrapperKey = `token-${keyIndex}`
    if (token.startsWith('```')) {
      segments.push(
        <Box
          component="span"
          key={wrapperKey}
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace',
            background: 'rgba(148, 163, 184, 0.2)',
            borderRadius: 1,
            px: 0.6,
            py: 0.2,
            whiteSpace: 'pre-wrap',
            display: 'inline-block',
          }}
        >
          {token.slice(3, -3)}
        </Box>,
      )
    } else if (token.startsWith('*')) {
      segments.push(
        <Box component="strong" key={wrapperKey} sx={{ fontWeight: 700 }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith('_')) {
      segments.push(
        <Box component="em" key={wrapperKey} sx={{ fontStyle: 'italic' }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else if (token.startsWith('~')) {
      segments.push(
        <Box component="span" key={wrapperKey} sx={{ textDecoration: 'line-through' }}>
          {token.slice(1, -1)}
        </Box>,
      )
    } else {
      const { cleanToken, trailingText } = splitTrailingUrlToken(token)
      const href = normalizeExternalUrl(cleanToken)
      segments.push(
        <Box
          component="a"
          key={wrapperKey}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{
            color: '#2563eb',
            textDecoration: 'underline',
            wordBreak: 'break-all',
          }}
        >
          {cleanToken}
        </Box>,
      )
      if (trailingText) {
        segments.push(
          <Fragment key={`${wrapperKey}-trail`}>
            {trailingText}
          </Fragment>,
        )
      }
    }

    keyIndex += 1
    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    segments.push(
      <Fragment key={`plain-${keyIndex}`}>
        {text.slice(lastIndex)}
      </Fragment>,
    )
  }

  return segments
}

function AttachmentPreview({ attachment }) {
  const fileName = attachment?.fileName || 'Attachment'
  const url = normalizeAttachmentUrl(attachment)
  const kind = getAttachmentKind(attachment)

  if (!url) {
    return null
  }

  if (kind === 'image') {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={url}
          alt={fileName}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 240,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  if (kind === 'audio') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: 'rgba(255,255,255,0.7)' }}>
        <audio controls preload="metadata" src={url} style={{ width: '100%' }} />
      </Box>
    )
  }

  if (kind === 'video') {
    return (
      <Box sx={{ borderRadius: 2, overflow: 'hidden', background: '#0f172a' }}>
        <video controls preload="metadata" src={url} style={{ width: '100%', maxHeight: 260 }} />
      </Box>
    )
  }

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      size="small"
      startIcon={getAttachmentIcon(kind)}
      endIcon={<OpenInNewRoundedIcon />}
      sx={{
        alignSelf: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: 2,
        textTransform: 'none',
      }}
    >
      {fileName}
    </Button>
  )
}

function LinkPreviewCard({ url }) {
  const href = normalizeExternalUrl(url)
  if (!href) {
    return null
  }

  let hostname = href
  let secondaryText = href
  try {
    const parsed = new URL(href)
    hostname = parsed.hostname.replace(/^www\./i, '')
    secondaryText = `${parsed.pathname || ''}${parsed.search || ''}${parsed.hash || ''}` || parsed.hostname
  } catch (error) {
    // Use the raw URL as fallback.
  }

  if (isImageLikeUrl(href)) {
    return (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noreferrer"
        sx={{
          display: 'block',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={href}
          alt={hostname}
          sx={{
            display: 'block',
            width: '100%',
            maxHeight: 220,
            objectFit: 'cover',
            background: '#e2e8f0',
          }}
        />
      </Box>
    )
  }

  return (
    <Paper
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      variant="outlined"
      sx={{
        px: 1.2,
        py: 1,
        borderRadius: 2,
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        borderColor: 'rgba(148, 163, 184, 0.22)',
        background: 'rgba(255, 255, 255, 0.7)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.75,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(226, 232, 240, 0.9)',
            color: '#0f172a',
            flexShrink: 0,
          }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f172a' }} noWrap>
            {hostname}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {secondaryText || href}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function buildWhatsappExternalLink(target, bookedEvent) {
  const rawNumber = `${target?.whatsappNumber || ''}`.replace(/[^0-9]/g, '')
  if (!rawNumber) {
    return ''
  }
  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'the patient'
  const dateLabel = bookedEvent?.startTime
    ? dayjs.utc(bookedEvent.startTime).local().format('D MMM YYYY, h:mm A')
    : 'the scheduled session'
  const message = `Hello ${target?.displayName || ''}, I am reaching out regarding ${patientName}'s booking on ${dateLabel}.`
  return `https://wa.me/${rawNumber}?text=${encodeURIComponent(message.trim())}`
}

function buildMailtoUri(target, bookedEvent) {
  if (!target?.email) {
    return ''
  }
  const patientName = bookedEvent?.patientInfoData?.name || bookedEvent?.userName || 'the patient'
  const sessionLabel = bookedEvent?.startTime
    ? dayjs.utc(bookedEvent.startTime).local().format('D MMM YYYY, h:mm A')
    : 'the scheduled session'
  const eventTypeLabel = bookedEvent?.eventName || bookedEvent?.eventType || 'appointment'
  const subject = `AUMHUM ${eventTypeLabel} follow-up`
  const body = [
    `Hello ${target?.displayName || ''},`,
    '',
    `I am reaching out regarding ${patientName}'s ${eventTypeLabel} on ${sessionLabel}.`,
    '',
    'Regards,',
    bookedEvent?.mentorName || 'AUMHUM',
  ].join('\n')
  return `mailto:${encodeURIComponent(target.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function ActionButton({
  label,
  icon,
  onClick,
  disabled = false,
  variant = 'outlined',
  color = 'primary',
  loading = false,
  title = '',
}) {
  const button = (
    <Button
      fullWidth
      size="small"
      variant={variant}
      color={color}
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : icon}
      sx={{
        minHeight: 34,
        justifyContent: 'center',
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 2.5,
        px: 0.9,
        fontSize: '0.78rem',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Button>
  )

  if (!title || !disabled) {
    return button
  }

  return (
    <Tooltip title={title}>
      <span>{button}</span>
    </Tooltip>
  )
}

function WhatsappThreadMessage({ item, outgoingLabel = 'Practitioner' }) {
  const incoming = item?.direction === 'incoming'
  const attachments = Array.isArray(item?.attachments) ? item.attachments : []
  const deliveryMeta = !incoming ? getDeliveryStatusMeta(item?.deliveryStatus || item?.status) : null
  const label = getTemplateActionFriendlyLabel(item?.actionType, item?.label)
  const rawMessage = `${item?.metadata?.rawMessage || ''}`.trim()
  const fallbackPreview = item?.messagePreview || 'No message preview was captured for this interaction.'
  const shouldRenderText = Boolean(rawMessage || attachments.length === 0)
  const messageText = rawMessage || (attachments.length === 0 ? fallbackPreview : '')
  const linkPreviews = extractMessageUrls(messageText).slice(0, 2)

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: incoming ? 'flex-start' : 'flex-end',
      }}
    >
      <Box
        sx={{
          maxWidth: '86%',
          px: 1.5,
          py: 1.2,
          borderRadius: 2.5,
          background: incoming
            ? 'rgba(241, 245, 249, 0.96)'
            : 'linear-gradient(135deg, rgba(220, 252, 231, 0.98) 0%, rgba(209, 250, 229, 0.95) 100%)',
          border: incoming
            ? '1px solid rgba(148, 163, 184, 0.18)'
            : '1px solid rgba(22, 163, 74, 0.18)',
          boxShadow: incoming
            ? '0 8px 24px rgba(15, 23, 42, 0.05)'
            : '0 10px 26px rgba(22, 163, 74, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {incoming ? item?.targetDisplayName || 'Recipient' : item?.practitionerName || outgoingLabel}
            </Typography>
            <Chip
              size="small"
              label={label}
              color={getTemplateActionChipColor(item?.actionType)}
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatThreadTimestamp(item?.createdAt)}
          </Typography>
        </Stack>

        {shouldRenderText ? (
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: '#111827',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.55,
            }}
          >
            {renderWhatsappMarkup(messageText)}
          </Typography>
        ) : null}

        {attachments.length > 0 ? (
          <Stack spacing={1} sx={{ mt: shouldRenderText ? 1 : 0.25 }}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                key={`${attachment?.fileId || attachment?.downloadUrl || 'attachment'}-${index}`}
                attachment={attachment}
              />
            ))}
          </Stack>
        ) : null}

        {linkPreviews.length > 0 ? (
          <Stack spacing={0.8} sx={{ mt: shouldRenderText || attachments.length > 0 ? 1 : 0.25 }}>
            {linkPreviews.map((url) => (
              <LinkPreviewCard key={url} url={url} />
            ))}
          </Stack>
        ) : null}

        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" sx={{ mt: 1, flexWrap: 'wrap' }}>
          {attachments.length > 0 ? (
            <Chip
              size="small"
              icon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
              label={`${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`}
              variant="outlined"
              sx={{ height: 22 }}
            />
          ) : null}
          {deliveryMeta ? (
            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ color: deliveryMeta.color }}>
              {deliveryMeta.icon}
              <Typography variant="caption" sx={{ color: deliveryMeta.color, fontWeight: 700 }}>
                {deliveryMeta.label}
              </Typography>
            </Stack>
          ) : null}
        </Stack>

        {!incoming && item?.deliveryErrorMessage ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: '#dc2626' }}>
            {item.deliveryErrorMessage}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
}

function ExternalContactPanel({ tabKey, target, bookedEvent, onCall }) {
  const tabMeta = getTabMeta(tabKey)
  const whatsappHref = buildWhatsappExternalLink(target, bookedEvent)
  const mailtoUri = buildMailtoUri(target, bookedEvent)
  const isSelfTarget = !!target?.isSelfTarget
  const canWhatsapp = !!target?.channels?.whatsapp?.enabled && !!whatsappHref && !isSelfTarget
  const canEmail = !!target?.channels?.email?.enabled && !!mailtoUri && !isSelfTarget
  const canCall = !!target?.channels?.call?.enabled && !!target?.phoneNumber && !isSelfTarget
  const availableActionCount = [canWhatsapp, canEmail, canCall].filter(Boolean).length
  const actionReason =
    target?.channels?.whatsapp?.reason
    || target?.channels?.email?.reason
    || target?.channels?.call?.reason
    || ''

  return (
    <Stack spacing={1.5}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${tabMeta.borderColor}`,
          background: tabMeta.bgColor,
          px: 1.5,
          py: 1.5,
        }}
      >
        <Stack spacing={0.9}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  background: tabMeta.badgeBg,
                  color: tabMeta.accentColor,
                }}
              >
                {tabMeta.icon}
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                  {target?.displayName || tabMeta.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {target?.contactSourceLabel || `${tabMeta.label} contact`}
                </Typography>
              </Box>
            </Stack>
            {isSelfTarget ? <Chip size="small" label="You" color="secondary" variant="outlined" /> : null}
          </Stack>

          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {target?.phoneNumber ? <Chip size="small" label={`Phone: ${formatTargetPhone(target.phoneNumber)}`} /> : null}
            {target?.whatsappNumber ? (
              <Chip size="small" color="success" label={`WhatsApp: ${formatTargetPhone(target.whatsappNumber)}`} />
            ) : null}
            {target?.email ? <Chip size="small" label={`Email: ${target.email}`} /> : null}
          </Stack>
        </Stack>
      </Paper>

      <Alert severity={isSelfTarget ? 'info' : 'success'}>
        {tabKey === 'patient'
          ? 'This booking supports the full in-portal WhatsApp thread below.'
          : isSelfTarget
            ? 'This row points to you, so there is no separate outbound contact target for calling or messaging.'
            : 'This tab keeps the same polished communication drawer, but WhatsApp for this contact currently hands off to the external channel instead of an in-portal thread.'}
      </Alert>

      {actionReason && !target?.whatsappNumber && !target?.email && !target?.phoneNumber ? (
        <Alert severity="warning">{actionReason}</Alert>
      ) : null}

      {availableActionCount > 0 ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {canWhatsapp ? (
            <ActionButton
              label="Open WhatsApp"
              icon={<LaunchRoundedIcon sx={{ fontSize: 16 }} />}
              color="success"
              onClick={() => {
                if (!whatsappHref) {
                  toast.error(target?.channels?.whatsapp?.reason || 'WhatsApp is not available for this contact.')
                  return
                }
                window.open(whatsappHref, '_blank', 'noopener,noreferrer')
              }}
            />
          ) : null}
          {canEmail ? (
            <ActionButton
              label="Open Email"
              icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                if (!mailtoUri) {
                  toast.error(target?.channels?.email?.reason || 'Email is not available for this contact.')
                  return
                }
                window.location.href = mailtoUri
              }}
            />
          ) : null}
          {canCall ? (
            <ActionButton
              label="Call"
              icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={onCall}
            />
          ) : null}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {actionReason || 'No direct outbound communication shortcut is available for this contact.'}
        </Typography>
      )}
    </Stack>
  )
}

function MentorBookingCommunicationHub({
  bookedEvent,
  sx = {},
  prefetchedTargets = null,
  prefetchedTargetsLoading = false,
  visibleTargetKeys = undefined,
  compactPatientActions = false,
  quietCompactPatientActions = false,
  hidePatientCallAndWhatsappActions = false,
  supportPopoverOnly = false,
}) {
  const [supportPopoverAnchorEl, setSupportPopoverAnchorEl] = useState(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const fileInputRef = useRef(null)
  const bookedEventId = bookedEvent?.id || ''
  const resolvedClinicBrandId = bookedEvent?.clinicBrandId || bookedEvent?.clinicBrandData?.id || ''
  const resolvedClinicId = bookedEvent?.clinicId || bookedEvent?.clinicData?.id || ''

  const [targetsState, setTargetsState] = useState({
    loading: false,
    error: '',
    targets: null,
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedTab, setSelectedTab] = useState('patient')
  const [patientThreadLoading, setPatientThreadLoading] = useState(false)
  const [patientThreadError, setPatientThreadError] = useState('')
  const [patientThreadState, setPatientThreadState] = useState({
    loaded: false,
    messages: [],
    templateActions: [],
    freeFormAllowed: false,
    freeFormWindowStartAt: null,
    freeFormWindowEndAt: null,
    targetDisplayName: '',
    targetWhatsappNumber: '',
  })
  const [selectedTemplateAction, setSelectedTemplateAction] = useState('')
  const [messageDraft, setMessageDraft] = useState('')
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [attachmentMenuAnchorEl, setAttachmentMenuAnchorEl] = useState(null)
  const [attachmentInputAccept, setAttachmentInputAccept] = useState('*/*')
  const [attachmentInputKey, setAttachmentInputKey] = useState(0)
  const [messageSending, setMessageSending] = useState(false)
  const [templateSending, setTemplateSending] = useState(false)
  const [busyActionKey, setBusyActionKey] = useState('')
  const [showTemplatePanel, setShowTemplatePanel] = useState(false)
  const resolvedVisibleTargetKeys = useMemo(() => {
    if (!Array.isArray(visibleTargetKeys) || visibleTargetKeys.length === 0) {
      return TAB_ORDER
    }
    return TAB_ORDER.filter((tabKey) => visibleTargetKeys.includes(tabKey))
  }, [visibleTargetKeys])

  const ensureTargetsLoaded = useCallback(
    async (options = {}) => {
      if (targetsState.targets && !options?.force) {
        return targetsState.targets
      }
      if (prefetchedTargets && !options?.force) {
        return prefetchedTargets
      }

      setTargetsState((currentValue) => ({
        ...currentValue,
        loading: true,
        error: '',
      }))

      const result = await getMentorCommunicationTargets(bookedEventId, options)
      if (result?.ok) {
        const nextTargets = result.targets || {}
        setTargetsState({
          loading: false,
          error: '',
          targets: nextTargets,
        })
        return nextTargets
      }

      const message = result?.message || 'Unable to load communication targets.'
      setTargetsState({
        loading: false,
        error: message,
        targets: null,
      })
      return null
    },
    [bookedEventId, prefetchedTargets, targetsState.targets],
  )

  useEffect(() => {
    setTargetsState({
      loading: false,
      error: '',
      targets: null,
    })
  }, [bookedEventId])

  const activeTargets = targetsState.targets || prefetchedTargets || {}
  const patientTarget = activeTargets?.patient || null
  const practitionerTarget = activeTargets?.practitioner || null
  const supportTarget = activeTargets?.support || null

  useEffect(() => {
    if (resolvedVisibleTargetKeys.includes(selectedTab)) {
      return
    }
    setSelectedTab(resolvedVisibleTargetKeys[0] || 'patient')
  }, [resolvedVisibleTargetKeys, selectedTab])

  const loadPatientThread = useCallback(
    async (options = {}) => {
      if (!bookedEventId) {
        return null
      }
      if (!options?.background) {
        setPatientThreadLoading(true)
        setPatientThreadError('')
      }

      const result = await getMentorPatientWhatsappThread(bookedEventId)
      if (result?.ok) {
        setPatientThreadState({
          loaded: true,
          messages: result.messages || [],
          templateActions: result.templateActions || [],
          freeFormAllowed: !!result.freeFormAllowed,
          freeFormWindowStartAt: result.freeFormWindowStartAt || null,
          freeFormWindowEndAt: result.freeFormWindowEndAt || null,
          targetDisplayName: result.targetDisplayName || patientTarget?.displayName || '',
          targetWhatsappNumber: result.targetWhatsappNumber || patientTarget?.whatsappNumber || '',
        })
        setPatientThreadError('')
        if (!selectedTemplateAction && result?.templateActions?.length) {
          setSelectedTemplateAction(result.templateActions[0].id)
        }
        if (result?.templateActions?.length > 0 && !result?.freeFormAllowed) {
          setShowTemplatePanel(true)
        }
      } else {
        setPatientThreadError(result?.message || 'Unable to load the WhatsApp conversation.')
      }
      setPatientThreadLoading(false)
      return result
    },
    [bookedEventId, patientTarget?.displayName, patientTarget?.whatsappNumber, selectedTemplateAction],
  )

  const openWhatsappDrawer = useCallback(
    async (tabKey) => {
      setBusyActionKey(`${tabKey}-whatsapp`)
      const targets = await ensureTargetsLoaded()
      setBusyActionKey('')
      if (!targets) {
        toast.error(targetsState.error || 'Unable to load communication targets.')
        return
      }
      const target = targets?.[tabKey]
      if (target?.channels?.whatsapp && !target.channels.whatsapp.enabled) {
        toast.error(target.channels.whatsapp.reason || 'WhatsApp is not available for this contact.')
        return
      }
      setSelectedTab(tabKey)
      setDrawerOpen(true)
      if (tabKey === 'patient') {
        await loadPatientThread()
      }
    },
    [ensureTargetsLoaded, loadPatientThread, targetsState.error],
  )

  const openMailTarget = useCallback(
    async (tabKey) => {
      setBusyActionKey(`${tabKey}-email`)
      const targets = await ensureTargetsLoaded()
      setBusyActionKey('')
      const target = targets?.[tabKey]
      const mailtoUri = buildMailtoUri(target, bookedEvent)
      if (!mailtoUri) {
        toast.error(target?.channels?.email?.reason || 'Email is not available for this contact.')
        return
      }
      window.location.href = mailtoUri
    },
    [bookedEvent, ensureTargetsLoaded],
  )

  const openPractitionerCall = useCallback(async () => {
    setBusyActionKey('practitioner-call')
    const targets = await ensureTargetsLoaded()
    setBusyActionKey('')
    const target = targets?.practitioner
    if (!target?.phoneNumber || !target?.channels?.call?.enabled) {
      toast.error(
        target?.channels?.call?.reason
          || (target?.isSelfTarget
            ? 'This row represents you, so there is no separate practitioner number to call.'
            : 'Calling is not available for this practitioner.'),
      )
      return
    }
    window.location.href = `tel:${target.phoneNumber}`
  }, [ensureTargetsLoaded])

  const handleSelectFiles = useCallback((event) => {
    const pickedFiles = Array.from(event?.target?.files || [])
    if (pickedFiles.length === 0) {
      return
    }
    setAttachmentFiles((currentValue) => [...currentValue, ...pickedFiles])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setAttachmentInputKey((currentValue) => currentValue + 1)
  }, [])

  const handleSendTemplate = useCallback(async () => {
    if (!selectedTemplateAction) {
      toast.error('Choose a template action first.')
      return
    }
    setTemplateSending(true)
    const result = await sendMentorPatientWhatsappTemplate(bookedEventId, selectedTemplateAction)
    setTemplateSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp template.')
      return
    }

    toast.success('Template sent on WhatsApp.')
    await loadPatientThread({ background: true })
  }, [bookedEventId, loadPatientThread, selectedTemplateAction])

  const handleSendPatientMessage = useCallback(async () => {
    if (!patientThreadState.freeFormAllowed) {
      toast.error('Send the approved opener first to unlock free-form chat.')
      return
    }
    if (!messageDraft.trim() && attachmentFiles.length === 0) {
      toast.error('Enter a message or attach a file before sending.')
      return
    }

    setMessageSending(true)
    const result = attachmentFiles.length > 0
      ? await sendMentorPatientWhatsappAttachment(bookedEventId, messageDraft.trim(), attachmentFiles)
      : await sendMentorPatientWhatsappMessage(bookedEventId, messageDraft.trim())
    setMessageSending(false)

    if (!result?.ok) {
      toast.error(result?.message || 'Unable to send the WhatsApp message.')
      return
    }

    setMessageDraft('')
    setAttachmentFiles([])
    await loadPatientThread({ background: true })
  }, [attachmentFiles, bookedEventId, loadPatientThread, messageDraft, patientThreadState.freeFormAllowed])

  const handleComposerKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      if (!messageSending) {
        handleSendPatientMessage()
      }
    }
  }, [handleSendPatientMessage, messageSending])

  const openAttachmentMenu = useCallback((event) => {
    setAttachmentMenuAnchorEl(event.currentTarget)
  }, [])

  const closeAttachmentMenu = useCallback(() => {
    setAttachmentMenuAnchorEl(null)
  }, [])

  const triggerAttachmentPicker = useCallback((option) => {
    setAttachmentInputAccept(option?.accept || '*/*')
    setAttachmentInputKey((currentValue) => currentValue + 1)
    closeAttachmentMenu()
    window.setTimeout(() => {
      fileInputRef.current?.click()
    }, 0)
  }, [closeAttachmentMenu])

  const removeAttachmentAt = useCallback((index) => {
    setAttachmentFiles((currentValue) => currentValue.filter((_, currentIndex) => currentIndex !== index))
  }, [])

  const activeTabTarget = useMemo(() => {
    if (selectedTab === 'patient') {
      return patientTarget
    }
    if (selectedTab === 'practitioner') {
      return practitionerTarget
    }
    return supportTarget
  }, [patientTarget, practitionerTarget, selectedTab, supportTarget])

  const patientWindowLabel = useMemo(() => {
    const endLabel = formatThreadTimestamp(patientThreadState?.freeFormWindowEndAt)
    if (!endLabel) {
      return ''
    }
    return patientThreadState?.freeFormAllowed ? `Reply window till ${endLabel}` : `Reply window closed on ${endLabel}`
  }, [patientThreadState?.freeFormAllowed, patientThreadState?.freeFormWindowEndAt])

  const rows = useMemo(() => {
    const patientName = patientTarget?.displayName
      || bookedEvent?.patientInfoData?.name
      || bookedEvent?.userName
      || 'Booked patient'
    const practitionerDisplayName = practitionerTarget?.displayName
      || bookedEvent?.mentorName
      || 'Assigned practitioner'
    const supportDisplayName = supportTarget?.displayName
      || bookedEvent?.clinicBrandData?.brandName
      || bookedEvent?.clinicBrandName
      || 'Support team'

    const allRows = [
      {
        key: 'patient',
        displayName: patientName,
        contextLabel: patientTarget?.contactSourceLabel || 'Booked patient',
        statusLabel: null,
        helperText: 'Call, chat, or email the participant attached to this booking.',
      },
      {
        key: 'practitioner',
        displayName: practitionerTarget?.isSelfTarget ? `${practitionerDisplayName} (You)` : practitionerDisplayName,
        contextLabel: practitionerTarget?.contactSourceLabel || 'Assigned practitioner',
        statusLabel: practitionerTarget?.isSelfTarget ? 'You' : null,
        helperText: practitionerTarget?.isSelfTarget
          ? 'This row represents you, so call/WhatsApp shortcuts stay disabled unless another practitioner is attached.'
          : 'Reach the other practitioner attached to this booking when one is available.',
      },
      {
        key: 'support',
        displayName: supportDisplayName,
        contextLabel: supportTarget?.contactSourceLabel || 'Support routing',
        statusLabel: null,
        helperText: 'Use the clinic or brand support channel for scheduling and operational help.',
      },
    ]
    return allRows.filter((row) => resolvedVisibleTargetKeys.includes(row.key))
  }, [
    bookedEvent?.clinicBrandData?.brandName,
    bookedEvent?.clinicBrandName,
    bookedEvent?.mentorName,
    bookedEvent?.patientInfoData?.name,
    bookedEvent?.userName,
    patientTarget?.contactSourceLabel,
    patientTarget?.displayName,
    practitionerTarget?.contactSourceLabel,
    practitionerTarget?.displayName,
    practitionerTarget?.isSelfTarget,
    resolvedVisibleTargetKeys,
    supportTarget?.contactSourceLabel,
    supportTarget?.displayName,
  ])

  const compactWhatsappDisabledReason =
    patientTarget?.channels?.whatsapp && !patientTarget.channels.whatsapp.enabled
      ? patientTarget.channels.whatsapp.reason || 'WhatsApp is not available for this patient.'
      : ''
  const compactMailDisabledReason =
    patientTarget?.channels?.email && !patientTarget.channels.email.enabled
      ? patientTarget.channels.email.reason || 'Email is not available for this patient.'
      : ''
  const compactActionsLoading = targetsState.loading || prefetchedTargetsLoading
  const compactWhatsappBusy =
    compactActionsLoading || busyActionKey === 'patient-whatsapp' || patientThreadLoading
  const compactMailBusy = compactActionsLoading || busyActionKey === 'patient-email'
  const compactCallEnabled = !!patientTarget?.channels?.call?.enabled
  const compactWhatsappEnabled = !!patientTarget?.channels?.whatsapp?.enabled
  const compactMailEnabled = !!patientTarget?.channels?.email?.enabled
  const hasCompactPatientAction = compactCallEnabled || compactWhatsappEnabled || compactMailEnabled

  const supportCallEnabled = !!supportTarget?.channels?.call?.enabled
  const supportWhatsappEnabled = !!supportTarget?.channels?.whatsapp?.enabled
  const supportPopoverOpen = Boolean(supportPopoverAnchorEl)
  const handleOpenSupportPopover = useCallback(async (event) => {
    event.stopPropagation()
    setSupportPopoverAnchorEl(event.currentTarget)
    await ensureTargetsLoaded()
  }, [ensureTargetsLoaded])
  const handleCloseSupportPopover = useCallback(() => {
    setSupportPopoverAnchorEl(null)
  }, [])

  if (compactPatientActions && !supportPopoverOnly && !compactActionsLoading && !hasCompactPatientAction) {
    return null
  }

  const quietCompactIconSx = quietCompactPatientActions
    ? {
        width: 30,
        height: 30,
        color: '#475569',
        background: 'rgba(248, 250, 252, 0.92)',
        border: '1px solid rgba(203, 213, 225, 0.9)',
        boxShadow: 'none',
        '&:hover': {
          background: 'rgba(241, 245, 249, 0.96)',
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          color: '#94a3b8',
          background: 'rgba(241, 245, 249, 0.72)',
        },
      }
    : null

  return (
    <>
      {supportPopoverOnly ? (
        <Box sx={{ display: 'inline-flex', flexShrink: 0, ...sx }} onClick={(event) => event.stopPropagation()}>
          <Tooltip title="Open support actions" arrow>
            <Button
              size="small"
              variant="outlined"
              onClick={handleOpenSupportPopover}
              startIcon={<SupportAgentRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                borderColor: 'rgba(13, 148, 136, 0.32)',
                color: '#0f766e',
                px: 1.5,
                minHeight: 32,
                background: 'linear-gradient(135deg, rgba(240, 253, 250, 0.95) 0%, rgba(236, 253, 245, 0.92) 100%)',
                '&:hover': {
                  borderColor: 'rgba(13, 148, 136, 0.5)',
                  background: 'linear-gradient(135deg, rgba(204, 251, 241, 0.95) 0%, rgba(209, 250, 229, 0.92) 100%)',
                },
              }}
            >
              Support
            </Button>
          </Tooltip>
          <Popover
            open={supportPopoverOpen}
            anchorEl={supportPopoverAnchorEl}
            onClose={handleCloseSupportPopover}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: -1,
                p: 1.25,
                minWidth: 240,
                borderRadius: 2.5,
                border: '1px solid rgba(13, 148, 136, 0.18)',
                background: 'linear-gradient(135deg, rgba(240, 253, 250, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
                boxShadow: '0 18px 40px rgba(15, 23, 42, 0.16)',
              },
            }}
          >
            <Stack spacing={0.9} onClick={(event) => event.stopPropagation()}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(13, 148, 136, 0.12)',
                    color: '#0f766e',
                    flexShrink: 0,
                  }}
                >
                  <SupportAgentRoundedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    Support
                  </Typography>
                  {supportTarget?.displayName ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.3 }}
                      noWrap
                    >
                      {supportTarget.displayName}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
              {compactActionsLoading && !supportTarget ? (
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ px: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Checking support channels...
                  </Typography>
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.max(1, [supportCallEnabled, supportWhatsappEnabled].filter(Boolean).length || 1)}, minmax(0, 1fr))`,
                    gap: 0.75,
                  }}
                >
                  {supportCallEnabled ? (
                    <PortalVoiceCallButton
                      clinicBrandId={resolvedClinicBrandId || undefined}
                      clinicId={resolvedClinicId || undefined}
                      uiSurface="mentor-booking-support-popover"
                      allowedDestinationTypes={['support-team']}
                      renderTrigger={({ onClick, busy }) => (
                        <ActionButton
                          label="Call"
                          icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
                          onClick={onClick}
                          loading={busy}
                          color="primary"
                        />
                      )}
                    />
                  ) : null}
                  {supportWhatsappEnabled ? (
                    <ActionButton
                      label="WhatsApp"
                      icon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        handleCloseSupportPopover()
                        openWhatsappDrawer('support')
                      }}
                      loading={busyActionKey === 'support-whatsapp'}
                      color="success"
                      variant="outlined"
                    />
                  ) : null}
                  {!supportCallEnabled && !supportWhatsappEnabled ? (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      {supportTarget?.channels?.call?.reason
                        || supportTarget?.channels?.whatsapp?.reason
                        || 'Support channels are not configured for this booking.'}
                    </Typography>
                  ) : null}
                </Box>
              )}
            </Stack>
          </Popover>
        </Box>
      ) : compactPatientActions ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            flexShrink: 0,
            ...sx,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {compactActionsLoading && !hasCompactPatientAction ? (
            <CircularProgress size={16} sx={{ color: '#64748b', mx: 0.75 }} />
          ) : null}
          {compactCallEnabled ? (
            <PortalVoiceCallButton
              bookedEventId={bookedEventId}
              clinicBrandId={resolvedClinicBrandId || undefined}
              clinicId={resolvedClinicId || undefined}
              uiSurface="mentor-booking-header-patient-icon"
              allowedDestinationTypes={['patient']}
              buttonLabel="Call patient"
              iconOnly
              size="small"
              sx={{
                width: 30,
                height: 30,
                minWidth: 30,
                boxShadow: quietCompactPatientActions ? 'none' : '0 8px 16px rgba(37, 99, 235, 0.18)',
                ...(quietCompactPatientActions
                  ? {
                      color: '#475569',
                      background: 'rgba(248, 250, 252, 0.92)',
                      border: '1px solid rgba(203, 213, 225, 0.9)',
                      '&:hover': {
                        background: 'rgba(241, 245, 249, 0.96)',
                        boxShadow: 'none',
                      },
                    }
                  : {}),
              }}
            />
          ) : null}
          {compactWhatsappEnabled ? (
            <Tooltip
              title={
                compactWhatsappDisabledReason ||
                (compactWhatsappBusy ? 'Checking WhatsApp...' : 'WhatsApp patient')
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="WhatsApp patient"
                  onClick={(event) => {
                    event.stopPropagation()
                    openWhatsappDrawer('patient')
                  }}
                  disabled={!bookedEventId || compactWhatsappBusy || !!compactWhatsappDisabledReason}
                  sx={{
                    ...(quietCompactIconSx || {
                      width: 30,
                      height: 30,
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.22)',
                      boxShadow: '0 8px 16px rgba(34, 197, 94, 0.18)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                        boxShadow: '0 10px 18px rgba(34, 197, 94, 0.24)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.72)',
                        background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.9) 0%, rgba(100, 116, 139, 0.9) 100%)',
                      },
                    }),
                  }}
                >
                  {compactWhatsappBusy ? (
                    <CircularProgress size={14} sx={{ color: 'inherit' }} />
                  ) : (
                    <WhatsAppIcon sx={{ fontSize: 17 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {compactMailEnabled ? (
            <Tooltip
              title={
                compactMailDisabledReason ||
                (compactMailBusy ? 'Preparing mail...' : 'Email patient')
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="Email patient"
                  onClick={(event) => {
                    event.stopPropagation()
                    openMailTarget('patient')
                  }}
                  disabled={!bookedEventId || compactMailBusy || !!compactMailDisabledReason}
                  sx={{
                    ...(quietCompactIconSx || {
                      width: 30,
                      height: 30,
                      color: '#ffffff',
                      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.22)',
                      boxShadow: '0 8px 16px rgba(37, 99, 235, 0.18)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                        boxShadow: '0 10px 18px rgba(37, 99, 235, 0.24)',
                      },
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.72)',
                        background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.9) 0%, rgba(100, 116, 139, 0.9) 100%)',
                      },
                    }),
                  }}
                >
                  {compactMailBusy ? (
                    <CircularProgress size={14} sx={{ color: 'inherit' }} />
                  ) : (
                    <EmailRoundedIcon sx={{ fontSize: 17 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            maxWidth: { xs: '100%', xl: 390 },
            display: 'grid',
            gap: 0.9,
            ...sx,
          }}
        >
        <Box
          sx={{
            px: 1.2,
            py: 0.95,
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(255, 255, 255, 1) 100%)',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="caption" sx={{ color: '#475569', letterSpacing: '0.08em', fontWeight: 800 }}>
            COMMUNICATION
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            Call, WhatsApp, and mail routes for this booking.
          </Typography>
        </Box>

        {rows
          .filter((row) => {
            if (!hidePatientCallAndWhatsappActions || row.key !== 'patient') {
              return true
            }
            return !!patientTarget?.channels?.email?.enabled
          })
          .map((row) => {
          const tabMeta = getTabMeta(row.key)
          const activeTarget =
            row.key === 'patient'
              ? patientTarget
              : row.key === 'practitioner'
                ? practitionerTarget
                : supportTarget
          const patientCallAvailable = row.key === 'patient' && !!bookedEventId
          const supportCallAvailable =
            row.key === 'support' && !!(resolvedClinicBrandId && resolvedClinicId)
          const practitionerCallDisabledReason =
            row.key === 'practitioner'
              ? (activeTarget?.channels?.call?.reason
                || (activeTarget?.isSelfTarget
                  ? 'You are the assigned practitioner for this booking.'
                  : targetsState.error))
              : ''
          const showCallAction =
            !(hidePatientCallAndWhatsappActions && row.key === 'patient') &&
            (patientCallAvailable ||
              supportCallAvailable ||
              (row.key === 'practitioner' ? !!activeTarget?.channels?.call?.enabled : !!activeTarget?.channels?.call?.enabled))
          const showWhatsappAction =
            !(hidePatientCallAndWhatsappActions && row.key === 'patient') &&
            !!activeTarget?.channels?.whatsapp?.enabled
          const showEmailAction = !!activeTarget?.channels?.email?.enabled
          const availableActionCount = [showCallAction, showWhatsappAction, showEmailAction].filter(Boolean).length
          const channelStatusLoading =
            !activeTarget &&
            !showCallAction &&
            (targetsState.loading || prefetchedTargetsLoading)
          const unavailableReason =
            practitionerCallDisabledReason
            || targetsState.error
            || activeTarget?.channels?.whatsapp?.reason
            || activeTarget?.channels?.email?.reason
            || activeTarget?.channels?.call?.reason
            || 'No direct communication shortcut is available for this contact.'

          return (
            <Paper
              key={row.key}
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${tabMeta.borderColor}`,
                background: tabMeta.bgColor,
                px: 1.1,
                py: 1,
                boxShadow: '0 8px 20px rgba(15, 23, 42, 0.045)',
              }}
            >
              <Stack spacing={0.9}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2,
                        display: 'grid',
                        placeItems: 'center',
                        background: tabMeta.badgeBg,
                        color: tabMeta.accentColor,
                        flexShrink: 0,
                      }}
                    >
                      {tabMeta.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 800,
                            color: '#0f172a',
                            lineHeight: 1.2,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {tabMeta.label}
                        </Typography>
                        {row.statusLabel ? (
                          <Chip
                            size="small"
                            label={row.statusLabel}
                            variant="outlined"
                            sx={{ height: 22, bgcolor: 'rgba(255,255,255,0.7)' }}
                          />
                        ) : null}
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0f172a',
                          fontWeight: 700,
                          mt: 0.1,
                          lineHeight: 1.25,
                        }}
                      >
                        {row.displayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          mt: 0.15,
                          lineHeight: 1.25,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.contextLabel || row.helperText}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>

                {channelStatusLoading ? (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Checking available channels...
                    </Typography>
                  </Stack>
                ) : availableActionCount > 0 ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${Math.max(1, availableActionCount)}, minmax(0, 1fr))`,
                      gap: 0.75,
                    }}
                  >
                    {showCallAction ? (
                      row.key === 'patient' ? (
                        <PortalVoiceCallButton
                          bookedEventId={bookedEventId}
                          clinicBrandId={resolvedClinicBrandId || undefined}
                          clinicId={resolvedClinicId || undefined}
                          uiSurface="mentor-booking-communication-grid"
                          allowedDestinationTypes={['patient']}
                          renderTrigger={({ onClick, busy }) => (
                            <ActionButton
                              label="Call"
                              icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
                              onClick={onClick}
                              loading={busy}
                              color="primary"
                            />
                          )}
                        />
                      ) : row.key === 'support' ? (
                        <PortalVoiceCallButton
                          clinicBrandId={resolvedClinicBrandId || undefined}
                          clinicId={resolvedClinicId || undefined}
                          uiSurface="mentor-booking-communication-grid"
                          allowedDestinationTypes={['support-team']}
                          renderTrigger={({ onClick, busy }) => (
                            <ActionButton
                              label="Call"
                              icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
                              onClick={onClick}
                              loading={busy}
                              color="primary"
                            />
                          )}
                        />
                      ) : (
                        <ActionButton
                          label="Call"
                          icon={<PhoneRoundedIcon sx={{ fontSize: 16 }} />}
                          onClick={openPractitionerCall}
                          loading={busyActionKey === 'practitioner-call'}
                          disabled={!!practitionerCallDisabledReason}
                          title={practitionerCallDisabledReason}
                          color="primary"
                        />
                      )
                    ) : null}

                    {showWhatsappAction ? (
                      <ActionButton
                        label="WhatsApp"
                        icon={<WhatsAppIcon sx={{ fontSize: 16 }} />}
                        onClick={() => openWhatsappDrawer(row.key)}
                        loading={busyActionKey === `${row.key}-whatsapp`}
                        color="success"
                        variant="outlined"
                      />
                    ) : null}

                    {showEmailAction ? (
                      <ActionButton
                        label="Mail"
                        icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => openMailTarget(row.key)}
                        loading={busyActionKey === `${row.key}-email`}
                        color="inherit"
                        variant="outlined"
                      />
                    ) : null}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {unavailableReason}
                  </Typography>
                )}
              </Stack>
            </Paper>
          )
        })}

        {targetsState.error ? (
          <Alert severity="warning">{targetsState.error}</Alert>
        ) : null}
        </Box>
      )}

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={() => {
          if (!patientThreadLoading && !templateSending && !messageSending) {
            setDrawerOpen(false)
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 620,
            maxWidth: '100%',
            height: isMobile ? '88vh' : '100%',
            borderTopLeftRadius: isMobile ? 20 : 0,
            borderTopRightRadius: isMobile ? 20 : 0,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background:
              'linear-gradient(180deg, rgba(248, 250, 252, 0.96) 0%, rgba(255, 255, 255, 1) 28%)',
          }}
        >
          <Box sx={{ px: 2.1, py: 1.5, borderBottom: '1px solid rgba(226, 232, 240, 1)', background: '#ffffff' }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    WhatsApp
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {activeTabTarget?.displayName || activeTabTarget?.title || getTabMeta(selectedTab).label}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                  {selectedTab === 'patient' ? (
                    <IconButton
                      size="small"
                      onClick={() => loadPatientThread()}
                      disabled={patientThreadLoading}
                      aria-label="Refresh"
                    >
                      <RefreshRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  <Button size="small" onClick={() => setDrawerOpen(false)} sx={{ minWidth: 0, px: 0.75 }}>
                    Close
                  </Button>
                </Stack>
              </Stack>

              <Tabs
                value={selectedTab}
                onChange={async (_, value) => {
                  setSelectedTab(value)
                  if (value === 'patient' && !patientThreadState.loaded) {
                    await loadPatientThread()
                  }
                }}
                variant="fullWidth"
                sx={{
                  minHeight: 42,
                  '& .MuiTab-root': {
                    minHeight: 42,
                    textTransform: 'none',
                    fontWeight: 700,
                  },
                }}
              >
                {resolvedVisibleTargetKeys.map((tabKey) => (
                  <Tab key={tabKey} value={tabKey} label={getTabMeta(tabKey).label} />
                ))}
              </Tabs>

              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={getTabMeta(selectedTab).label} />
                {activeTabTarget?.phoneNumber ? (
                  <Chip size="small" label={`Phone: ${formatTargetPhone(activeTabTarget.phoneNumber)}`} />
                ) : null}
                {activeTabTarget?.whatsappNumber ? (
                  <Chip size="small" color="success" label={`WhatsApp: ${formatTargetPhone(activeTabTarget.whatsappNumber)}`} />
                ) : null}
                {activeTabTarget?.email ? <Chip size="small" label={`Email: ${activeTabTarget.email}`} /> : null}
              </Stack>
            </Stack>
          </Box>

          {selectedTab === 'patient' ? (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  background: '#ffffff',
                }}
              >
                <Box sx={{ px: { xs: 1.25, md: 2 }, py: 1.25 }}>
                  <Stack spacing={1}>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.7,
                        borderRadius: 2,
                        background: patientThreadState?.freeFormAllowed
                          ? 'rgba(220, 252, 231, 0.5)'
                          : 'rgba(254, 249, 195, 0.7)',
                        border: patientThreadState?.freeFormAllowed
                          ? '1px solid rgba(34, 197, 94, 0.12)'
                          : '1px solid rgba(245, 158, 11, 0.18)',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                        <Typography
                          variant="caption"
                          sx={{
                            color: patientThreadState?.freeFormAllowed ? '#166534' : '#92400e',
                            fontWeight: 700,
                            lineHeight: 1.4,
                          }}
                        >
                          {patientThreadState?.freeFormAllowed
                            ? 'Free-form window is open. You can send messages and attachments.'
                            : 'Send an approved opener to start a new WhatsApp conversation.'}
                        </Typography>
                        {patientThreadState?.templateActions?.length > 0 ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setShowTemplatePanel((currentValue) => !currentValue)}
                            sx={{ textTransform: 'none' }}
                          >
                            {showTemplatePanel ? 'Hide' : 'Send approved'}
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>

                    {patientWindowLabel ? (
                      <Typography variant="caption" color="text.secondary">
                        {patientWindowLabel}
                      </Typography>
                    ) : null}

                    {patientThreadState?.templateActions?.length > 0 ? (
                      <Collapse in={showTemplatePanel}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch" sx={{ pt: 0.5 }}>
                          <TextField
                            select
                            fullWidth
                            size="small"
                            label="Approved message"
                            value={selectedTemplateAction}
                            onChange={(event) => setSelectedTemplateAction(event.target.value)}
                          >
                            {patientThreadState.templateActions.map((action) => (
                              <MenuItem key={action?.id} value={action?.id}>
                                {action?.label || action?.id}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={templateSending ? <CircularProgress size={14} color="inherit" /> : <WhatsAppIcon />}
                            onClick={handleSendTemplate}
                            disabled={!selectedTemplateAction || templateSending}
                            sx={{ minWidth: { xs: '100%', sm: 120 }, flexShrink: 0 }}
                          >
                            Send
                          </Button>
                        </Stack>
                      </Collapse>
                    ) : null}
                  </Stack>
                </Box>

                <Divider />

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    px: { xs: 1.25, md: 2 },
                    py: 1.5,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    scrollBehavior: 'smooth',
                    background:
                      'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 1) 18%)',
                  }}
                >
                  {patientThreadLoading ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', flex: 1 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : patientThreadError ? (
                    <Alert severity="warning">{patientThreadError}</Alert>
                  ) : patientThreadState?.messages?.length > 0 ? (
                    patientThreadState.messages.map((item, index) => (
                      <WhatsappThreadMessage
                        key={item?.id || item?.messageSid || `${item?.createdAt || 'message'}-${index}`}
                        item={item}
                        outgoingLabel={bookedEvent?.mentorName || 'Practitioner'}
                      />
                    ))
                  ) : (
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: '1px dashed rgba(148, 163, 184, 0.4)',
                        background: 'rgba(248, 250, 252, 0.9)',
                        px: 2,
                        py: 3,
                        textAlign: 'center',
                      }}
                    >
                      <HistoryEduRoundedIcon sx={{ fontSize: 30, color: '#94a3b8', mb: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        No WhatsApp interactions yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {patientThreadState?.freeFormAllowed
                          ? 'Free-form window is already open for this patient. You can send a message below.'
                          : 'Start with the approved opener so the patient sees a clean first message.'}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider />

                <Box sx={{ px: { xs: 1.25, md: 2 }, py: 1.5, background: '#ffffff' }}>
                  <Stack spacing={1.1}>
                    {attachmentFiles.length > 0 ? (
                      <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.25 }}>
                        {attachmentFiles.map((file, index) => {
                          const attachmentMeta = describePendingAttachment(file)
                          return (
                            <Paper
                              key={`${file?.name || 'file'}-${index}`}
                              elevation={0}
                              sx={{
                                minWidth: 180,
                                maxWidth: 260,
                                px: 1,
                                py: 0.75,
                                borderRadius: 2.5,
                                border: '1px solid rgba(203, 213, 225, 0.9)',
                                background: '#ffffff',
                                flexShrink: 0,
                              }}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    display: 'grid',
                                    placeItems: 'center',
                                    background: 'rgba(226, 232, 240, 0.9)',
                                    color: '#0f172a',
                                    flexShrink: 0,
                                  }}
                                >
                                  {getComposerAttachmentIcon(attachmentMeta.kind)}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0f172a' }} noWrap>
                                    {file?.name || `Attachment ${index + 1}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {attachmentMeta.secondaryLabel}
                                  </Typography>
                                </Box>
                                <IconButton size="small" onClick={() => removeAttachmentAt(index)} sx={{ flexShrink: 0 }}>
                                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Stack>
                            </Paper>
                          )
                        })}
                      </Stack>
                    ) : null}

                    <Paper
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 0.5,
                        px: 0.75,
                        py: 0.5,
                        borderRadius: 3,
                        border: '1px solid rgba(203, 213, 225, 0.9)',
                        background: '#ffffff',
                        boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
                      }}
                    >
                      <Tooltip title="Attach">
                        <span>
                          <IconButton
                            size="small"
                            onClick={openAttachmentMenu}
                            disabled={!patientThreadState?.freeFormAllowed || messageSending}
                            aria-label="Choose attachment type"
                            sx={{
                              width: 38,
                              height: 38,
                              color: '#54656f',
                            }}
                          >
                            <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={6}
                          variant="standard"
                          value={messageDraft}
                          onChange={(event) => setMessageDraft(event.target.value)}
                          onKeyDown={handleComposerKeyDown}
                          placeholder={patientThreadState?.freeFormAllowed ? 'Type a message' : 'Send an approved opener first'}
                          disabled={!patientThreadState?.freeFormAllowed || messageSending}
                          InputProps={{ disableUnderline: true }}
                          sx={{
                            '& .MuiInputBase-root': {
                              px: 0.5,
                              py: 0.75,
                              alignItems: 'flex-end',
                              fontSize: '0.95rem',
                              lineHeight: 1.5,
                            },
                            '& textarea': {
                              resize: 'none',
                            },
                          }}
                        />
                      </Box>

                      <Tooltip title={!messageDraft.trim() && attachmentFiles.length === 0 ? 'Type a message or attach a file' : 'Send'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleSendPatientMessage}
                            disabled={messageSending || (!messageDraft.trim() && attachmentFiles.length === 0)}
                            aria-label="Send message"
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: messageSending || (!messageDraft.trim() && attachmentFiles.length === 0)
                                ? 'rgba(148, 163, 184, 0.18)'
                                : '#00a884',
                              color: messageSending || (!messageDraft.trim() && attachmentFiles.length === 0)
                                ? '#94a3b8'
                                : '#ffffff',
                              '&:hover': {
                                bgcolor: messageSending || (!messageDraft.trim() && attachmentFiles.length === 0)
                                  ? 'rgba(148, 163, 184, 0.24)'
                                  : '#019270',
                              },
                            }}
                          >
                            {messageSending ? (
                              <CircularProgress size={16} sx={{ color: 'inherit' }} />
                            ) : (
                              <SendRoundedIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Paper>

                    <input
                      ref={fileInputRef}
                      key={`${attachmentInputKey}-${attachmentInputAccept}`}
                      hidden
                      multiple
                      type="file"
                      accept={attachmentInputAccept}
                      onChange={handleSelectFiles}
                    />
                    <Menu
                      anchorEl={attachmentMenuAnchorEl}
                      open={Boolean(attachmentMenuAnchorEl)}
                      onClose={closeAttachmentMenu}
                      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      PaperProps={{
                        sx: {
                          mt: -1,
                          borderRadius: 3,
                          minWidth: 250,
                          boxShadow: '0 20px 48px rgba(15, 23, 42, 0.18)',
                          border: '1px solid rgba(226, 232, 240, 0.9)',
                          p: 0.5,
                        },
                      }}
                    >
                      {WHATSAPP_ATTACHMENT_OPTIONS.map((option) => (
                        <MenuItem key={option.id} onClick={() => triggerAttachmentPicker(option)} sx={{ borderRadius: 2, py: 1 }}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                display: 'grid',
                                placeItems: 'center',
                                background: 'rgba(240, 242, 245, 0.95)',
                                color: '#0f172a',
                                flexShrink: 0,
                              }}
                            >
                              {getComposerAttachmentIcon(option.kind, 20)}
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                {option.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.helper}
                              </Typography>
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Stack>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, px: 2, py: 2, overflowY: 'auto' }}>
              <ExternalContactPanel
                tabKey={selectedTab}
                target={activeTabTarget}
                bookedEvent={bookedEvent}
                onCall={selectedTab === 'practitioner'
                  ? openPractitionerCall
                  : () => toast.info('Use the call button in the support row to open the live support call flow.')}
              />
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  )
}

export default memo(MentorBookingCommunicationHub)
